var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import localStorageAccessor from "@utility/local-storage-accessor";
import { isTokenExpired, getUserInfoFromToken } from "@utility/jwt-decoder";
import { Mutex } from "async-mutex";
// Mutex to prevent multiple simultaneous refresh attempts
const refreshMutex = new Mutex();
// Shared refresh state across interceptors
let isRefreshing = false;
let refreshPromise = null;
const redirectToLogin = () => {
    localStorageAccessor.removeAccessToken();
    localStorageAccessor.removeRefreshToken();
    localStorageAccessor.removeUser();
    window.location.href = "/insight/login";
};
/**
 * Proactively refresh the access token if it's expired
 * This is called BEFORE making API requests to ensure we always have a valid token
 */
const refreshTokenIfNeeded = (api) => __awaiter(void 0, void 0, void 0, function* () {
    const token = localStorageAccessor.getAccessToken();
    const refreshToken = localStorageAccessor.getRefreshToken();
    // No token means not authenticated
    if (!token) {
        return null;
    }
    // Token is still valid, no need to refresh
    if (!isTokenExpired(token)) {
        return token;
    }
    // Token is expired but no refresh token available
    if (!refreshToken) {
        redirectToLogin();
        return null;
    }
    // Use mutex to prevent race conditions with multiple simultaneous requests
    return refreshMutex.runExclusive(() => __awaiter(void 0, void 0, void 0, function* () {
        // Double-check after acquiring mutex - another request might have refreshed
        const currentToken = localStorageAccessor.getAccessToken();
        if (currentToken && !isTokenExpired(currentToken)) {
            return currentToken;
        }
        // If already refreshing, wait for the existing promise
        if (isRefreshing && refreshPromise) {
            return refreshPromise;
        }
        isRefreshing = true;
        const userEmail = localStorageAccessor.getUserEmail();
        const currentRefreshToken = localStorageAccessor.getRefreshToken();
        if (!currentRefreshToken || !userEmail) {
            isRefreshing = false;
            redirectToLogin();
            return null;
        }
        refreshPromise = (() => __awaiter(void 0, void 0, void 0, function* () {
            var _a, _b, _c, _d, _e;
            try {
                const response = yield api.post("Auth/refresh-access-token", {
                    email: userEmail,
                    refreshToken: currentRefreshToken,
                });
                if (!((_a = response.data) === null || _a === void 0 ? void 0 : _a.isSuccess)) {
                    const status = (_b = response.data) === null || _b === void 0 ? void 0 : _b.status;
                    if (status === 401 || status === 403) {
                        throw new Error(((_c = response.data) === null || _c === void 0 ? void 0 : _c.message) || "Refresh token expired");
                    }
                    throw new Error(((_d = response.data) === null || _d === void 0 ? void 0 : _d.message) || "Token refresh failed");
                }
                if ((_e = response.data) === null || _e === void 0 ? void 0 : _e.data) {
                    const newAccessToken = response.data.data;
                    localStorageAccessor.setAccessToken(newAccessToken);
                    // Update user info from the new token
                    const userInfo = getUserInfoFromToken(newAccessToken);
                    if (userInfo) {
                        const roles = Array.isArray(userInfo.role)
                            ? userInfo.role
                            : userInfo.role
                                ? [userInfo.role]
                                : [];
                        localStorageAccessor.setUser({
                            id: userInfo.sub,
                            name: `${userInfo.firstName} ${userInfo.lastName}`,
                            email: userInfo.email,
                            userName: userInfo.userName,
                            firstName: userInfo.firstName,
                            lastName: userInfo.lastName,
                            tenantId: userInfo.tenantId,
                            roles: roles,
                            permissions: userInfo.permission || [],
                        });
                    }
                    return newAccessToken;
                }
                throw new Error("No access token in response");
            }
            catch (error) {
                console.error("Token refresh failed:", error);
                redirectToLogin();
                return null;
            }
            finally {
                isRefreshing = false;
                refreshPromise = null;
            }
        }))();
        return refreshPromise;
    }));
});
export const attachRequestInterceptor = (api) => {
    api.interceptors.request.use((config) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        // Skip token handling for auth endpoints that don't require authentication
        if (((_a = config.url) === null || _a === void 0 ? void 0 : _a.includes("send-otp")) ||
            ((_b = config.url) === null || _b === void 0 ? void 0 : _b.includes("verify-otp")) ||
            ((_c = config.url) === null || _c === void 0 ? void 0 : _c.includes("resend-otp")) ||
            ((_d = config.url) === null || _d === void 0 ? void 0 : _d.includes("refresh-access-token"))) {
            return config;
        }
        // Proactively refresh token if expired (before making the request)
        const validToken = yield refreshTokenIfNeeded(api);
        if (validToken && config.headers) {
            config.headers.set("Authorization", `Bearer ${validToken}`);
        }
        return config;
    }));
    return api;
};
// Export for use by response interceptor as fallback
export { refreshTokenIfNeeded, redirectToLogin };
